import { FormEvent, KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { CiChat2 } from 'react-icons/ci';
import { FiMinus, FiSend, FiX } from 'react-icons/fi';

import { useAuth } from 'context/AuthContext';
import { FOCO_VISIVEL } from 'utils/foco';

import AvatarDoJJ from './AvatarDoJJ';
import ConfirmarEncerramento from './ConfirmarEncerramento';
import MessageChatIA from './messageChatIA';
import { useConversaComJJ } from './useConversaComJJ';

const TEMPO_DO_BALAO = 10000;
const TEMPO_DO_AVISO = 2000;

// Fechar do cabeçalho da casca dos modais (`ModalFrame`): círculo branco com ícone azul e anel amarelo sobre o azul.
const BOTAO_DO_CABECALHO =
  'flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-jj-full border-0 bg-brand-white text-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-yellow';

/**
 * Assistente virtual JJ: o botão "Fale com a gente" (com o balão de abertura nos primeiros 10 s) e a
 * janela da conversa, um painel não modal no canto inferior direito. O visual segue a casca dos
 * modais das telas migradas; o Claude Design não tem tela do chat.
 */
export default function ChatIA() {
  const { user } = useAuth();
  const { mensagens, respondendo, enviar, pedirOutraResposta, encerrar } = useConversaComJJ();

  const [mostrarBalao, setMostrarBalao] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [texto, setTexto] = useState('');
  const [aviso, setAviso] = useState('');

  const janelaId = useId();
  const tituloId = useId();
  const campoId = useId();
  const botaoRef = useRef<HTMLButtonElement>(null);
  const campoRef = useRef<HTMLInputElement>(null);
  const encerrarRef = useRef<HTMLButtonElement>(null);
  const conversaRef = useRef<HTMLDivElement>(null);
  const conteudoRef = useRef<HTMLDivElement>(null);
  const jaAbriu = useRef(false);
  const estavaConfirmando = useRef(false);

  useEffect(() => {
    const temporizador = setTimeout(() => setMostrarBalao(false), TEMPO_DO_BALAO);
    return () => clearTimeout(temporizador);
  }, []);

  // CHAT-21 e CHAT-22: abrir leva o foco ao campo; fechar o devolve ao botão.
  useEffect(() => {
    if (aberto) {
      jaAbriu.current = true;
      campoRef.current?.focus();
    } else if (jaAbriu.current) {
      botaoRef.current?.focus();
    }
  }, [aberto]);

  // CHAT-25: ao sair da confirmação sem encerrar, o foco volta ao "Encerrar chat" do cabeçalho.
  useEffect(() => {
    if (estavaConfirmando.current && !confirmando && aberto) encerrarRef.current?.focus();
    estavaConfirmando.current = confirmando;
  }, [confirmando, aberto]);

  // `inert` tira a janela do Tab e da árvore de acessibilidade enquanto a confirmação está aberta. Vai pelo
  // DOM, e não como prop: o React 18 não conhece a prop, e o React 19 a trata como booleana (o `''` sumiria).
  useEffect(() => {
    conteudoRef.current?.toggleAttribute('inert', confirmando);
  }, [confirmando, aberto]);

  // CHAT-07: o aviso de cópia sai do `role="status"` depois de 2 s, para poder ser anunciado de novo.
  useEffect(() => {
    if (!aviso) return;
    const temporizador = setTimeout(() => setAviso(''), TEMPO_DO_AVISO);
    return () => clearTimeout(temporizador);
  }, [aviso]);

  // CHAT-10
  useEffect(() => {
    if (conversaRef.current) conversaRef.current.scrollTop = conversaRef.current.scrollHeight;
  }, [mensagens, respondendo, aberto]);

  if (!user?.extra?.id) return null;

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    if (enviar(texto)) setTexto('');
  }

  function aoTeclar(evento: KeyboardEvent) {
    if (evento.key !== 'Escape') return;
    if (confirmando) setConfirmando(false);
    else if (aberto) setAberto(false);
  }

  // Fechar a janela por qualquer caminho cancela uma confirmação de encerramento pendente.
  function alternarJanela() {
    setConfirmando(false);
    setAberto(atual => !atual);
  }

  function confirmarEncerramento() {
    encerrar();
    setTexto('');
    setConfirmando(false);
    setAberto(false);
  }

  return (
    <div onKeyDown={aoTeclar}>
      {/* CHAT-20: o mesmo contêiner com e sem o balão, e o botão sempre no mesmo lugar dentro dele. Assim o
          React não remonta o botão quando o balão some, e quem chegou a ele pelo Tab não perde o foco. */}
      <div
        className={`fixed bottom-8 right-8 flex items-center ${
          mostrarBalao
            ? 'jj-help-card w-[275px] flex-row justify-center gap-2 rounded-2xl bg-brand-white p-4 shadow-jj-help-card sm:w-[330px] sm:gap-4'
            : 'rounded-jj-full shadow-jj-help'
        }`}
      >
        <button
          ref={botaoRef}
          type="button"
          className={`jj-help flex h-[54px] w-[54px] shrink-0 cursor-pointer items-center justify-center rounded-jj-full border-0 bg-brand-blue text-brand-yellow ${FOCO_VISIVEL}`}
          onClick={alternarJanela}
          aria-label="Fale com a gente"
          aria-expanded={aberto}
          aria-controls={aberto ? janelaId : undefined}
        >
          <CiChat2 aria-hidden="true" size={30} />
        </button>
        {mostrarBalao && (
          <p className="text-sm text-content">
            <b className="text-brand-blue">Olá, sou o assistente virtual JJ</b>.<br /> Como posso te ajudar hoje?
          </p>
        )}
      </div>

      {aberto && (
        <div
          id={janelaId}
          role="dialog"
          aria-labelledby={tituloId}
          className="fixed bottom-4 right-4 flex h-[80%] max-h-[700px] w-[calc(100%-32px)] max-w-[400px] flex-col overflow-hidden rounded-jj-2xl bg-brand-white shadow-jj-modal-brand lg:right-12 xl:max-w-[600px]"
        >
          <div ref={conteudoRef} className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-3 bg-brand-blue px-4 py-3">
              <AvatarDoJJ tamanho="grande" />
              {/* 20px, como o título do `ModalFrame`: amarelo sobre o azul dá 4,44:1, e só passa como texto grande (WCAG 1.4.3). */}
              <h2
                id={tituloId}
                className="m-0 min-w-0 flex-1 font-display text-xl font-extrabold leading-tight text-brand-yellow"
              >
                Chat com o assistente JJ
              </h2>
              <button type="button" className={BOTAO_DO_CABECALHO} onClick={() => setAberto(false)} aria-label="Minimizar chat">
                <FiMinus aria-hidden="true" size={17} strokeWidth={2.8} />
              </button>
              <button
                ref={encerrarRef}
                type="button"
                className={BOTAO_DO_CABECALHO}
                onClick={() => setConfirmando(true)}
                aria-label="Encerrar chat"
              >
                <FiX aria-hidden="true" size={17} strokeWidth={2.8} />
              </button>
            </div>

            <div
              ref={conversaRef}
              role="log"
              aria-label="Conversa com o assistente JJ"
              className="min-h-0 flex-1 overflow-y-auto bg-surface-100 px-4 py-4"
            >
              <ul className="m-0 flex list-none flex-col gap-4 p-0">
                {mensagens.map((mensagem, indice) => (
                  <MessageChatIA
                    key={indice}
                    mensagem={mensagem}
                    respondendo={respondendo}
                    onPedirOutra={pedirOutraResposta}
                    onCopiada={() => setAviso('Resposta copiada')}
                  />
                ))}
              </ul>
              {respondendo && (
                <div aria-hidden="true" className="mt-4 flex items-end gap-2">
                  <AvatarDoJJ tamanho="pequeno" />
                  <span className="flex gap-1.5 rounded-jj-xl rounded-bl-jj-xs bg-brand-white px-4 py-4 shadow-jj-01">
                    {[0, 1, 2].map(ponto => (
                      <span
                        key={ponto}
                        className="h-2 w-2 animate-bounce rounded-jj-full bg-brand-blue motion-reduce:animate-none"
                        style={{ animationDelay: `${ponto * 150}ms` }}
                      />
                    ))}
                  </span>
                </div>
              )}
            </div>
            <span role="status" className="sr-only">
              {respondendo ? 'O assistente JJ está respondendo' : aviso}
            </span>

            <form onSubmit={aoEnviar} className="shrink-0 border-0 border-t border-solid border-surface-200 px-4 pt-4">
              <label htmlFor={campoId} className="sr-only">
                Mensagem para o assistente JJ
              </label>
              <div className="flex items-center gap-2 rounded-jj-pill border border-solid border-line bg-brand-white py-1 pl-5 pr-1 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-blue-dark">
                <input
                  ref={campoRef}
                  id={campoId}
                  type="text"
                  placeholder="Digite sua mensagem..."
                  value={texto}
                  onChange={evento => setTexto(evento.target.value)}
                  className="h-11 min-w-0 flex-1 border-0 bg-transparent text-sm text-content outline-none placeholder:text-content-soft"
                />
                <button
                  type="submit"
                  disabled={!texto.trim() || respondendo}
                  aria-label="Enviar mensagem"
                  className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-jj-full border-0 bg-brand-blue text-brand-yellow disabled:cursor-not-allowed disabled:bg-surface-300 disabled:text-content-soft ${FOCO_VISIVEL}`}
                >
                  <FiSend aria-hidden="true" size={18} />
                </button>
              </div>
            </form>
            <p className="m-0 px-5 pb-4 pt-2 text-center text-xs leading-normal text-content-soft">
              O JJ responde somente perguntas sobre o JogaJunto e pode cometer erros. Para respostas mais assertivas, especifique
              em sua pergunta a qual semestre você se refere. Caso sua dúvida não seja resolvida, reformule sua pergunta.
            </p>
          </div>

          {confirmando && <ConfirmarEncerramento onEncerrar={confirmarEncerramento} onVoltar={() => setConfirmando(false)} />}
        </div>
      )}
    </div>
  );
}
